/**
 * ImportFromKBModal — copy documents from another Knowledge Base into the
 * current one. Pick a source KB, select documents across (client-paginated)
 * pages or tick "import the whole collection", then import. Import is a COPY
 * (documents + their L1 units, embeddings included) with no re-processing, so
 * the docs land `completed` and ground in an exploration like native ones.
 */
import { useState, useEffect, useMemo } from 'react';
import {
  ComposedModal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Dropdown,
  Checkbox,
  InlineNotification,
  Loading,
  Pagination,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
} from '@carbon/react';
import { knowledgeBaseApi } from '@/lib/api/knowledge-base.api';
import { useKnowledgeBases, useImportDocuments } from '@/lib/query/hooks/useKnowledgeBases';
import type { KnowledgeBase, KBDocument } from '@shared/types/knowledge-base';

interface ImportFromKBModalProps {
  open: boolean;
  onClose: () => void;
  targetKbId: string;
  targetKbName?: string;
}

export function ImportFromKBModal({
  open,
  onClose,
  targetKbId,
  targetKbName,
}: ImportFromKBModalProps) {
  const { data: knowledgeBases } = useKnowledgeBases();
  const importMutation = useImportDocuments(targetKbId);

  const [sourceKbId, setSourceKbId] = useState<string | null>(null);
  const [documents, setDocuments] = useState<KBDocument[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [importAll, setImportAll] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ imported: number; skipped: number } | null>(null);

  // Candidate sources: every KB except the one we're importing into.
  const sourceKbs = useMemo(
    () => (knowledgeBases ?? []).filter((kb) => kb.id !== targetKbId),
    [knowledgeBases, targetKbId]
  );

  useEffect(() => {
    if (!open) {
      setSourceKbId(null);
      setDocuments([]);
      setSelected(new Set());
      setImportAll(false);
      setPage(1);
      setError(null);
      setResult(null);
    }
  }, [open]);

  // Load the source KB's completed documents (only completed ones have units).
  useEffect(() => {
    if (!sourceKbId) {
      setDocuments([]);
      return;
    }
    setDocsLoading(true);
    setSelected(new Set());
    setPage(1);
    setResult(null);
    knowledgeBaseApi
      .listDocuments(sourceKbId)
      .then((docs) => setDocuments(docs.filter((d) => d.conversion_status === 'completed')))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load documents'))
      .finally(() => setDocsLoading(false));
  }, [sourceKbId]);

  const pageDocs = documents.slice((page - 1) * pageSize, page * pageSize);
  const allPageSelected = pageDocs.length > 0 && pageDocs.every((d) => selected.has(d.id));
  const somePageSelected = pageDocs.some((d) => selected.has(d.id));

  const toggle = (id: string) =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const togglePage = (checked: boolean) =>
    setSelected((prev) => {
      const n = new Set(prev);
      pageDocs.forEach((d) => (checked ? n.add(d.id) : n.delete(d.id)));
      return n;
    });

  const canImport =
    !!sourceKbId && (importAll || selected.size > 0) && !importMutation.isPending && !result;

  const handleImport = async () => {
    if (!sourceKbId) return;
    setError(null);
    try {
      const res = await importMutation.mutateAsync(
        importAll ? { sourceKbId, all: true } : { sourceKbId, documentIds: [...selected] }
      );
      setResult({ imported: res.imported, skipped: res.skipped });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed');
    }
  };

  return (
    <ComposedModal open={open} onClose={onClose} size="lg">
      <ModalHeader
        title="Import from another Knowledge Base"
        label={targetKbName ? `Into: ${targetKbName}` : undefined}
      />
      <ModalBody hasScrollingContent>
        {error && (
          <InlineNotification
            kind="error"
            title="Error"
            subtitle={error}
            lowContrast
            onCloseButtonClick={() => setError(null)}
          />
        )}
        {result && (
          <InlineNotification
            kind="success"
            lowContrast
            title="Imported"
            subtitle={`${result.imported} document(s) imported, ${result.skipped} skipped (already present).`}
          />
        )}

        <Dropdown<KnowledgeBase>
          id="import-source-kb"
          titleText="Source Knowledge Base"
          label="Select a knowledge base"
          items={sourceKbs}
          itemToString={(kb) => (kb ? kb.name : '')}
          selectedItem={sourceKbs.find((kb) => kb.id === sourceKbId) ?? undefined}
          onChange={({ selectedItem }) => setSourceKbId(selectedItem?.id ?? null)}
        />

        {sourceKbId && (
          <div style={{ marginTop: '1rem' }}>
            <Checkbox
              id="import-all"
              labelText={`Import the whole collection${
                documents.length ? ` (${documents.length} documents)` : ''
              }`}
              checked={importAll}
              onChange={(_evt, { checked }) => setImportAll(checked)}
            />

            {docsLoading ? (
              <Loading small withOverlay={false} description="Loading documents" />
            ) : documents.length === 0 ? (
              <p style={{ marginTop: '0.5rem' }}>No completed documents in this knowledge base.</p>
            ) : (
              <div
                style={{
                  marginTop: '0.5rem',
                  opacity: importAll ? 0.5 : 1,
                  pointerEvents: importAll ? 'none' : 'auto',
                }}
              >
                <Table size="sm">
                  <TableHead>
                    <TableRow>
                      <TableHeader style={{ width: '2.5rem' }}>
                        <Checkbox
                          id="import-select-page"
                          hideLabel
                          labelText="Select all on this page"
                          checked={allPageSelected}
                          indeterminate={!allPageSelected && somePageSelected}
                          onChange={(_evt, { checked }) => togglePage(checked)}
                        />
                      </TableHeader>
                      <TableHeader>Document</TableHeader>
                      <TableHeader>Units</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageDocs.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell>
                          <Checkbox
                            id={`import-row-${doc.id}`}
                            hideLabel
                            labelText={`Select ${doc.filename}`}
                            checked={selected.has(doc.id)}
                            onChange={() => toggle(doc.id)}
                          />
                        </TableCell>
                        <TableCell>{doc.filename}</TableCell>
                        <TableCell>{doc.knowledge_unit_count ?? 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Pagination
                  page={page}
                  pageSize={pageSize}
                  pageSizes={[10, 25, 50]}
                  totalItems={documents.length}
                  onChange={({ page: p, pageSize: ps }) => {
                    setPage(p);
                    setPageSize(ps);
                  }}
                  size="sm"
                />
                <p style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}>
                  {selected.size} selected across all pages
                </p>
              </div>
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button kind="secondary" onClick={onClose}>
          Close
        </Button>
        <Button
          kind="primary"
          disabled={!canImport}
          onClick={result ? onClose : handleImport}
        >
          {importMutation.isPending ? 'Importing…' : result ? 'Done' : 'Import'}
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
}
